import { z } from "zod";
import { isValid } from "iban";
import {
  countries,
  currencies,
  defaultCountry,
  defaultCurrency,
  defaultDocumentLocale,
  defaultTimezone,
  documentLocales,
  invoicingTypes,
  payoutTypes,
  plugTypes,
  timezones,
  PayoutType,
} from "@evenli-platform/ui/lib/constants";
import { components } from "@evenli-platform/core/clients/evenli/evenli-ops";
import type {
  RealEstateSiteInvoiceLineItemMetadata,
  UserChargingTransactionLineItemMetadata,
  FleetInvoiceLineItemMetadata,
} from "@evenli-platform/core/services/invoices/types";
import { stringTrimmedNotEmptyNullable } from "@evenli-platform/ui/lib/utils-custom";

type InvoiceProduct = components["schemas"]["InvoiceProduct"];
type InvoiceCharge = components["schemas"]["InvoiceCharge"];
type InvoiceChargingTransaction =
  components["schemas"]["InvoiceChargingTransaction"];

type InvoiceLineItemMetadata =
  | RealEstateSiteInvoiceLineItemMetadata
  | UserChargingTransactionLineItemMetadata
  | FleetInvoiceLineItemMetadata;

export const AddressSchema = z.object({
  streetAddress: z.string().trim().min(2).max(70),
  postalCode: z.string().trim().min(2).max(10),
  city: z.string().trim().min(2).max(35),
  country: z.enum(countries),
});
export type AddressType = z.infer<typeof AddressSchema>;

export type CompanyNameAndIdType = {
  name: string;
  businessId: string;
};

export const GeneralInfoFormSchema = z.object({
  name: z.string().trim().min(2, "company_name_is_required"),
  businessId: z.string().trim().min(1).max(35),
  ...AddressSchema.shape,
  documentLocale: z.enum(documentLocales),
  internalComment: stringTrimmedNotEmptyNullable().default(null),
});

export type GeneralInfoFormType = z.infer<typeof GeneralInfoFormSchema>;

export const BillingFormSchema = z
  .object({
    businessId: z.string().trim().min(1).max(35),
    businessName: z.string().trim().min(1).max(70),
    emailAddress: z.string().trim().email(),
    contactPersonName: stringTrimmedNotEmptyNullable().optional(),
    invoicingDeliveryType: z.enum(invoicingTypes),
    documentLocale: z.enum(documentLocales),
    einvoicingAddress: stringTrimmedNotEmptyNullable(35).optional(),
    einvoicingOperator: stringTrimmedNotEmptyNullable(35).optional(),
    netPaymentTerm: z.number().int().min(0),
    currency: z.enum(currencies).default(defaultCurrency),
    buyerReference: stringTrimmedNotEmptyNullable().optional(),
    orderIdentifier: stringTrimmedNotEmptyNullable().optional(),
    isVatExempt: z.boolean(),
    additionalInformation: stringTrimmedNotEmptyNullable().optional(),
    ...AddressSchema.shape,
  })
  .refine(
    (data) => {
      return (
        data.invoicingDeliveryType !== "einvoice" || !!data.einvoicingAddress
      );
    },
    {
      message: "this_field_is_required",
      path: ["einvoicingAddress"],
    },
  )
  .refine(
    (data) => {
      return (
        data.invoicingDeliveryType !== "einvoice" || !!data.einvoicingOperator
      );
    },
    {
      message: "this_field_is_required",
      path: ["einvoicingOperator"],
    },
  );

export type BillingFormType = z.infer<typeof BillingFormSchema>;

export const PayoutsFormSchema = z
  .object({
    ...AddressSchema.shape,
    bankAccountNumber: z
      .string()
      .trim()
      .refine((iban) => isValid(iban), { message: "invalid_iban" }),
    bic: z.string().trim().min(1).max(11, "bic_must_be_at_most_11_characters"),
    emailAddress: z.string().trim().email().nullable().default(null),
    payoutType: z.enum(payoutTypes),
    documentLocale: z.enum(documentLocales),
    businessName: stringTrimmedNotEmptyNullable().default(null),
    businessId: stringTrimmedNotEmptyNullable().default(null),
    personFirstName: stringTrimmedNotEmptyNullable().default(null),
    personLastName: stringTrimmedNotEmptyNullable().default(null),
    referenceText: stringTrimmedNotEmptyNullable(70).default(null),
  })
  .refine(
    (data) => {
      return data.payoutType !== PayoutType.BUSINESS || !!data.businessName;
    },
    {
      message: "this_field_is_required",
      path: ["businessName"],
    },
  )
  .refine(
    (data) => {
      return data.payoutType !== PayoutType.BUSINESS || !!data.businessId;
    },
    {
      message: "this_field_is_required",
      path: ["businessId"],
    },
  )
  .refine(
    (data) => {
      return data.payoutType !== PayoutType.PERSON || !!data.personFirstName;
    },
    {
      message: "this_field_is_required",
      path: ["personFirstName"],
    },
  )
  .refine(
    (data) => {
      return data.payoutType !== PayoutType.PERSON || !!data.personLastName;
    },
    {
      message: "this_field_is_required",
      path: ["personLastName"],
    },
  );
export type PayoutsFormType = z.infer<typeof PayoutsFormSchema>;

export const EditBillingSchema = z
  .object({
    businessId: z.string().trim().min(1).max(35),
    businessName: z.string().trim().min(1).max(70),
    emailAddress: z.string().trim().email(),
    contactPersonName: stringTrimmedNotEmptyNullable().optional(),
    invoicingDeliveryType: z.enum(invoicingTypes),
    documentLocale: z.enum(documentLocales),
    einvoicingAddress: stringTrimmedNotEmptyNullable(35).optional(),
    einvoicingOperator: stringTrimmedNotEmptyNullable(35).optional(),
    netPaymentTerm: z.number().int().min(0),
    currency: z.enum(currencies).default(defaultCurrency),
    buyerReference: stringTrimmedNotEmptyNullable().optional(),
    orderIdentifier: stringTrimmedNotEmptyNullable().optional(),
    isVatExempt: z.boolean(),
    additionalInformation: stringTrimmedNotEmptyNullable().optional(),
    ...AddressSchema.shape,
  })
  .refine(
    (data) => {
      return (
        data.invoicingDeliveryType !== "einvoice" || !!data.einvoicingAddress
      );
    },
    {
      message: "this_field_is_required",
      path: ["einvoicingAddress"],
    },
  )
  .refine(
    (data) => {
      return (
        data.invoicingDeliveryType !== "einvoice" || !!data.einvoicingOperator
      );
    },
    {
      message: "this_field_is_required",
      path: ["einvoicingOperator"],
    },
  );

export type EditBillingInfoType = z.infer<typeof EditBillingSchema>;

export const EditPayoutsSchema = z
  .object({
    businessName: stringTrimmedNotEmptyNullable().default(null),
    businessId: stringTrimmedNotEmptyNullable().default(null),
    streetAddress: z.string().trim().min(1),
    postalCode: z.string().trim().min(1),
    city: z.string().trim().min(1),
    country: z.enum(countries),
    bankAccountNumber: z
      .string()
      .refine((iban) => isValid(iban), { message: "invalid_iban" }),
    bic: z.string().trim().min(1).max(11, "bic_must_be_at_most_11_characters"),
    emailAddress: z.string().trim().email().nullable().default(null),
    documentLocale: z.enum(documentLocales),
    personFirstName: stringTrimmedNotEmptyNullable().default(null),
    personLastName: stringTrimmedNotEmptyNullable().default(null),
    payoutType: z.enum(payoutTypes),
    referenceText: stringTrimmedNotEmptyNullable(70).default(null),
  })
  .refine(
    (data) => {
      return data.payoutType !== PayoutType.BUSINESS || !!data.businessName;
    },
    {
      message: "this_field_is_required",
      path: ["businessName"],
    },
  )
  .refine(
    (data) => {
      return data.payoutType !== PayoutType.BUSINESS || !!data.businessId;
    },
    {
      message: "this_field_is_required",
      path: ["businessId"],
    },
  )
  .refine(
    (data) => {
      return data.payoutType !== PayoutType.PERSON || !!data.personFirstName;
    },
    {
      message: "this_field_is_required",
      path: ["personFirstName"],
    },
  )
  .refine(
    (data) => {
      return data.payoutType !== PayoutType.PERSON || !!data.personLastName;
    },
    {
      message: "this_field_is_required",
      path: ["personLastName"],
    },
  );
export type EditPayoutsInfoType = z.infer<typeof EditPayoutsSchema>;

// Schema for SIGN IN form
export const signInSchema = z.object({
  email: z.string().email({ message: "enter_a_valid_email_address" }),
  password: z.string().min(8, "password_must_be_at_least_8_characters"),
});
export type SignInSchemaType = z.infer<typeof signInSchema>;

// Schema for CODE REQUEST form
export const codeRequestFormSchema = z.object({
  email: z.string().email({ message: "enter_a_valid_email_address" }),
});
export type CodeRequestSchemaType = z.infer<typeof codeRequestFormSchema>;

// Schema for RESET PASSWORD form
export const newPasswordSchema = z
  .object({
    code: z.string().min(1, "this_field_is_required"),
    password: z
      .string()
      .min(8, "password_must_be_at_least_8_characters")
      .refine((value) => /[a-z]/.test(value), {
        message: "password_must_contain_lowercase",
      })
      .refine((value) => /[A-Z]/.test(value), {
        message: "password_must_contain_uppercase",
      })
      .refine((value) => /[0-9]/.test(value), {
        message: "password_must_contain_number",
      })
      .refine((value) => /[\W_]/.test(value), {
        message: "password_must_contain_special_character",
      }),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "passwords_must_match",
    path: ["confirmPassword"],
  });
export type NewPasswordSchemaType = z.infer<typeof newPasswordSchema>;

// Schema for INVOICE form
export const InvoiceFormSchema = z.object({
  issuedAt: z.date(),
  dueDate: z.date(),
  netPaymentTerm: z.number().min(1),
  buyerReference: z.string().optional(),
  sellerReference: z.string().optional(),
  sellerFreeText: z.string().optional(),
  freeTextStart: z.string().optional(),
  freeTextEnd: z.string().optional(),
  billingAddress: z.string().optional(),
  billingPostalCode: z.string().optional(),
  billingCity: z.string().optional(),
  billingCountry: z.string().optional(),
  billingBusinessName: z.string().optional(),
  billingBusinessId: z.string().optional(),
  billingEinvoicingAddress: z.string().optional(),
  billingEinvoicingOperator: z.string().optional(),
  billingEmailAddress: z.string().optional(),
  billingContactPersonName: z.string().optional(),
  deliveryAddress: z.string().optional(),
  deliveryPostalCode: z.string().optional(),
  deliveryCity: z.string().optional(),
  deliveryCountry: z.string().optional(),
  deliveryBusinessName: z.string().optional(),
  deliveryBusinessId: z.string().optional(),
  agreementIdentifier: z.string().optional(),
  orderIdentifier: z.string().optional(),
  receiptNumber: z.string().optional(),
  referenceNumber: z.string().optional(),
  buyerIdentifier: z.string().optional(),
  invoiceType: z.string().optional(),
  subscriptionId: z.string().optional(),
  invoicingDeliveryType: z.enum(invoicingTypes).optional(),
  documentLocale: z.enum(documentLocales).optional(),
  timezone: z.enum(["UTC"]).optional(),
  invoiceLines: z.array(
    z
      .object({
        productId: z.number().nullable().optional(),
        chargeId: z.number().nullable().optional(),
        invoiceDisplayName: z.string().min(1),
        description: z.string().optional().nullable(),
        quantity: z.number(),
        unitPrice: z.number().min(0),
        tax: z.object({
          code: z.string().min(1),
        }),
        chargingTransactionId: z.number().nullable().optional(),
        metadata: z.custom<InvoiceLineItemMetadata>().optional(),
        product: z.custom<InvoiceProduct>().optional(),
        charge: z.custom<InvoiceCharge>().optional(),
        chargingTransaction: z.custom<InvoiceChargingTransaction>().optional(),
        siteId: z.number().nullable(),
        uniqueItemCount: z.number().nullable().optional().default(null),
      })
      .refine(
        (data) => {
          return (
            (data.productId !== null &&
              data.productId !== undefined &&
              (data.chargeId === null || data.chargeId === undefined)) ||
            (data.chargeId !== null &&
              data.chargeId !== undefined &&
              (data.productId === null || data.productId === undefined))
          );
        },
        {
          message: "Either a product or a charge must be selected",
          path: ["productId"],
        },
      ),
  ),
});
export type InvoiceForm = z.infer<typeof InvoiceFormSchema>;

export enum Status {
  NotInitialized = "",
  InvitationNotFound = "Invitation not found",
  SomethingWentWrong = "Something went wrong",
  Authorized = "Authorized user",
  Unauthorized = "Unauthorized user",
}

export type Pricing = components["schemas"]["Pricing"];

export const billingInfoSchema = z
  .object({
    isAnotherCompany: z.boolean(),
    billingBusinessName: z.string().nullable().optional(),
    billingBusinessId: z.string().nullable().optional(),
    billingEmail: z.string().email(),
    billingEinvoicingAddress: z.string().min(1),
    billingEinvoicingOperator: z.string().min(1),
    billingAdditionalInformation: z.string().optional(),
    billingIsVatExempt: z.boolean(),
    billingAddressLine: z.string().optional(),
    billingCity: z.string().optional(),
    billingPostalCode: z.string().optional(),
    billingCountry: z.enum(countries).optional(),
    billingBankAccountNumber: z
      .string()
      .refine((iban) => isValid(iban), { message: "invalid_iban" })
      .optional(),
    billingBic: z
      .string()
      .max(11, "bic_must_be_at_most_11_characters")
      .optional(),
    billingCurrency: z.enum(currencies).default(defaultCurrency),
    isAnotherPostalAddress: z.boolean(),
    netPaymentTerm: z.number().optional(),
    billingContactPersonName: z.string().optional(),
    billingBuyerReference: z.string().optional(),
    billingOrderIdentifier: z.string().optional(),
  })
  // .refine(
  //   (data) => {
  //     return !data.isAnotherCompany || !!data.billingBusinessName;
  //   },
  //   {
  //     message: "this_field_is_required",
  //     path: ["billingBusinessName"],
  //   },
  // )
  .refine(
    (data) => {
      return !data.isAnotherCompany || !!data.billingBusinessId;
    },
    {
      message: "this_field_is_required",
      path: ["billingBusinessId"],
    },
  )
  .refine(
    (data) => {
      return !data.isAnotherPostalAddress || !!data.billingAddressLine;
    },
    {
      message: "this_field_is_required",
      path: ["billingAddressLine"],
    },
  )
  .refine(
    (data) => {
      return !data.isAnotherPostalAddress || !!data.billingCity;
    },
    {
      message: "this_field_is_required",
      path: ["billingCity"],
    },
  )
  .refine(
    (data) => {
      return !data.isAnotherPostalAddress || !!data.billingCountry;
    },
    {
      message: "this_field_is_required",
      path: ["billingCountry"],
    },
  )
  .refine(
    (data) => {
      return !data.isAnotherPostalAddress || !!data.billingPostalCode;
    },
    {
      message: "this_field_is_required",
      path: ["billingPostalCode"],
    },
  );
export type BillingInfoSchemaType = z.infer<typeof billingInfoSchema>;

export const orgDetailsSchema = z.object({
  name: z.string().min(1, "company_name_is_required"),
  businessId: z.string().min(1),
  country: z.enum(countries).default(defaultCountry),
  city: z.string().min(1, "city_is_required"),
  streetAddress: z.string().min(1, "this_field_is_required"),
  postalCode: z.string().min(5, "min_5_digits"),
  timezone: z.enum(timezones).default(defaultTimezone),
  documentLocale: z.enum(documentLocales).default(defaultDocumentLocale),
});

export type OrgDetailsSchemaType = z.infer<typeof orgDetailsSchema>;

export type OrgFormValues = z.infer<typeof orgDetailsSchema>;

export const socketSchema = z.object({
  externalId: z.number(),
  invoiceable: z.boolean(),
  leasing: z.boolean(),
  description: stringTrimmedNotEmptyNullable().optional(),
  parkingSpaceNumber: stringTrimmedNotEmptyNullable(100).optional(),
  maxPower: z.number().min(0).optional(),
  plugType: z.enum(plugTypes),
  identifiers: z.array(
    z.object({
      id: z.string(),
      text: z.string(),
    }),
  ),
});

export type SocketStatus =
  | "Charging"
  | "Available"
  | "Preparing"
  | "Unavailable"
  | "Finishing"
  | "Booting"
  | "Offline"
  | "Error"
  | "SuspendedCAR"
  | "SuspendedCHARGER";

export enum EntityType {
  RENTER = "renter",
  PAYER = "payer",
}

export interface LoadMeasurementDevice {
  name: string;
  id: string;
  energyConsumptionLimit: string;
  maxCurrentLimit: string;
  offlineCurrentLimit: string;
}

export type InstallationLocations = Array<{
  id: string;
  name: string;
  frontFuseName?: string;
  frontFuseSize?: string;
  additionalInfo?: string;
  loadMeasurementDevice?: LoadMeasurementDevice;
  frontFuseLoadMeasurementDevice?: LoadMeasurementDevice;
  groups: Array<{
    id: string;
    name: string;
    fuseName: string;
    fuseSize: string;
    phaseOrder?: string;
    fuseAdditionalInfo: string | LoadMeasurementDevice;
    devices: Array<{
      id: string;
      details: {
        chargingStationIds: string;
        serialNumber: string;
        installationDate: string;
        qrCode: string;
        qrCode2: string;
        additionalInfo: string;
        phaseOrder?: string;
      };
    }>;
  }>;
}>;
